import { useState } from "react";
import { Lock, Eye, EyeOff, Check, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DashboardLayout } from "@/pages/DashboardPage";
import { useAuth, API } from "@/App";
import axios from "axios";

export default function PasswordAnalyzerPage() {
  const { token } = useAuth();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleAnalyze = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const headers = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const resp = await axios.post(`${API}/tools/password-analyze`, { password }, { headers, withCredentials: true });
      setResult(resp.data);
    } catch {
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const strengthColors = {
    strong: { bg: "bg-emerald-500", text: "text-emerald-400", label: "STRONG" },
    good: { bg: "bg-blue-500", text: "text-blue-400", label: "GOOD" },
    fair: { bg: "bg-amber-500", text: "text-amber-400", label: "FAIR" },
    weak: { bg: "bg-red-500", text: "text-red-400", label: "WEAK" },
  };

  const sc = result ? strengthColors[result.strength] || strengthColors.weak : null;

  const criteria = result ? [
    { label: "Lowercase letters", met: result.has_lowercase },
    { label: "Uppercase letters", met: result.has_uppercase },
    { label: "Numbers", met: result.has_numbers },
    { label: "Special characters", met: result.has_special },
    { label: "Minimum 8 characters", met: result.length >= 8 },
    { label: "12+ characters", met: result.length >= 12 },
  ] : [];

  return (
    <DashboardLayout title="Password Analyzer">
      <div className="max-w-2xl space-y-6" data-testid="password-analyzer-page">
        <div className="trace-border rounded-sm bg-zinc-900 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-sm bg-emerald-600/10 flex items-center justify-center">
              <Lock className="w-5 h-5 text-emerald-500" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Password Strength Analyzer</h2>
              <p className="text-xs text-zinc-500">Check how secure your password really is</p>
            </div>
          </div>
          <form onSubmit={handleAnalyze} className="flex gap-3">
            <div className="flex-1 relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password to analyze..."
                className="bg-zinc-950 border-zinc-800 focus:ring-1 focus:ring-blue-500 rounded-sm h-11 text-zinc-50 placeholder:text-zinc-600 pr-10 font-mono"
                required
                data-testid="password-input"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-500 text-white h-11 px-6 rounded-sm font-semibold" data-testid="password-analyze-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Analyze"}
            </Button>
          </form>
        </div>

        {result && (
          <div className="space-y-4 fade-up" data-testid="password-results">
            {/* Score */}
            <div className="trace-border rounded-sm bg-zinc-900 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <span className={`text-xs uppercase tracking-[0.2em] font-bold ${sc.text}`}>{sc.label}</span>
                  <div className="text-3xl font-black tracking-tighter mt-1" style={{ fontFamily: 'var(--font-heading)' }}>
                    <span className={sc.text}>{result.score}</span>
                    <span className="text-zinc-600 text-lg">/100</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">Crack Time</div>
                  <div className="text-lg font-bold text-zinc-200 mt-1 font-mono">{result.crack_time_estimate}</div>
                </div>
              </div>
              <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div className={`h-full ${sc.bg} transition-all duration-700`} style={{ width: `${result.score}%` }} />
              </div>
            </div>

            {/* Criteria */}
            <div className="trace-border rounded-sm bg-zinc-900 p-6">
              <h3 className="text-sm font-semibold tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Criteria</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {criteria.map((c) => (
                  <div key={c.label} className="flex items-center gap-2 text-sm">
                    {c.met ? (
                      <Check className="w-4 h-4 text-emerald-400" strokeWidth={2} />
                    ) : (
                      <X className="w-4 h-4 text-red-400" strokeWidth={2} />
                    )}
                    <span className={c.met ? "text-zinc-300" : "text-zinc-500"}>{c.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Feedback */}
            {result.feedback?.length > 0 && (
              <div className="trace-border rounded-sm bg-zinc-900 p-6">
                <h3 className="text-sm font-semibold tracking-tight mb-4" style={{ fontFamily: 'var(--font-heading)' }}>Suggestions</h3>
                <div className="space-y-2">
                  {result.feedback.map((f, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-amber-400">
                      <span className="text-amber-500 mt-0.5">&#x2022;</span> {f}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
